import React, { useMemo, useState } from 'react';
import { Search, Shield, Lock, LockOpen, ExternalLink, Trash2 } from 'lucide-react';
import { billingLabel, billingPillClass, SectionHeader } from '../shared';
import type { SuperAdminContext } from '../types';

function formatPlanEnd(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' });
}

function planEndHint(iso: string | null | undefined): string {
  if (!iso) return '';
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (days < 0) return `${Math.abs(days)}d ago`;
  if (days === 0) return 'today';
  return `${days}d`;
}

function endToneClass(iso: string | null | undefined): string {
  if (!iso) return 'text-zinc-400';
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
  if (days < 0) return 'text-rose-600 font-semibold';
  if (days <= 7) return 'text-amber-700 font-semibold';
  return 'text-zinc-600';
}

export function TenantsSection({ ctx }: { ctx: SuperAdminContext }) {
  const [query, setQuery] = useState('');
  const summaryById = useMemo(
    () => new Map(ctx.tenantSummaries.map((t) => [t.organizationId, t])),
    [ctx.tenantSummaries]
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ctx.orgs;
    return ctx.orgs.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        o.adminEmail.toLowerCase().includes(q) ||
        o.slug.toLowerCase().includes(q) ||
        o.id.toLowerCase().includes(q)
    );
  }, [ctx.orgs, query]);

  return (
    <div className="space-y-5">
      <SectionHeader
        title="Tenant registry"
        subtitle="Every organization on the platform — billing, storage, and usage at a glance."
      />

      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-400" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search name, email, slug, id…"
          className="h-9 w-full rounded-lg border border-zinc-200 bg-white py-0 pl-9 pr-3 text-sm shadow-sm focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/15"
        />
      </div>

      <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
        <div className="flex items-center gap-2 border-b border-zinc-100 bg-zinc-50/80 px-4 py-3">
          <Shield className="h-4 w-4 text-emerald-700" />
          <h3 className="text-sm font-bold text-zinc-800">All tenants</h3>
          <span className="ml-auto rounded-full bg-zinc-200/70 px-2.5 py-0.5 text-xs font-bold text-zinc-600">
            {filtered.length}
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm" style={{ minWidth: 1200 }}>
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50/60 text-xs font-bold uppercase tracking-wide text-zinc-500">
                <th className="sticky left-0 z-10 bg-zinc-50/95 px-4 py-3">Organization</th>
                <th className="whitespace-nowrap px-4 py-3">Plan</th>
                <th className="whitespace-nowrap px-4 py-3">Status</th>
                <th className="whitespace-nowrap px-4 py-3">Ends</th>
                <th className="whitespace-nowrap px-4 py-3">Access</th>
                <th className="whitespace-nowrap px-4 py-3 text-right">Media</th>
                <th className="whitespace-nowrap px-4 py-3 text-right">DB</th>
                <th className="whitespace-nowrap px-4 py-3 text-right">Recs</th>
                <th className="whitespace-nowrap px-4 py-3 text-right">SMS</th>
                <th className="whitespace-nowrap px-4 py-3">Last active</th>
                <th className="whitespace-nowrap px-4 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100/80">
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-4 py-10 text-center text-base text-zinc-400">
                    No tenants found.
                  </td>
                </tr>
              ) : (
                filtered.map((o) => {
                  const u = summaryById.get(o.id);
                  const expiresAt = u?.subscriptionExpiresAt ?? o.subscriptionExpiresAt;
                  return (
                    <tr
                      key={o.id}
                      className="group transition-colors hover:bg-emerald-50/40"
                    >
                      <td className="sticky left-0 z-10 bg-white px-4 py-3 group-hover:bg-emerald-50/40">
                        <div className="flex items-center gap-2.5">
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-sm font-bold text-white">
                            {o.name.charAt(0).toUpperCase()}
                          </div>
                          <p
                            className="max-w-[200px] truncate text-sm font-semibold text-zinc-900"
                            title={`${o.name} · ${o.slug}`}
                          >
                            {o.name}
                          </p>
                        </div>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-zinc-700">
                        {u?.planName || o.planId || '—'}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <span className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-bold ring-1 ${billingPillClass(o.subscriptionStatus)}`}>
                          {billingLabel(o.subscriptionStatus)}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        <span className={`text-sm tabular-nums ${endToneClass(expiresAt)}`}>
                          {formatPlanEnd(expiresAt)}
                          {planEndHint(expiresAt) ? (
                            <span className="ml-1 text-xs opacity-70">({planEndHint(expiresAt)})</span>
                          ) : null}
                        </span>
                      </td>

                      <td className="whitespace-nowrap px-4 py-3">
                        {o.accountHold ? (
                          <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/10 px-2 py-0.5 text-xs font-bold text-rose-700 ring-1 ring-rose-500/20">
                            <Lock className="h-3.5 w-3.5" /> Hold
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs font-bold text-emerald-800 ring-1 ring-emerald-500/15">
                            <LockOpen className="h-3.5 w-3.5" /> OK
                          </span>
                        )}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums text-zinc-600">
                        {u ? `${u.storage.mediaStorageUsedMb}/${u.storage.mediaStorageLimitMb}` : '—'}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums text-zinc-600">
                        {u ? `${u.storage.databaseStorageUsedMb}/${u.storage.databaseStorageLimitMb}` : '—'}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums font-medium text-zinc-800">
                        {u?.records.total ?? o.dataKeys ?? 0}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-right text-sm tabular-nums font-medium text-zinc-800">
                        {u?.smsMessagesInPeriod ?? u?.whatsappMessagesInPeriod ?? o.whatsappMessagesInPeriod ?? 0}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-zinc-500">
                        {o.lastWorkspaceAt
                          ? new Date(o.lastWorkspaceAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
                          : '—'}
                      </td>

                      <td className="whitespace-nowrap px-4 py-3 text-center">
                        <div className="inline-flex items-center justify-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => void ctx.openTenantUsage(o)}
                            disabled={ctx.usageLoadingFor === o.id}
                            className="inline-flex items-center gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-sm font-bold text-emerald-800 transition-colors hover:bg-emerald-100 disabled:opacity-50"
                          >
                            <ExternalLink className="h-3.5 w-3.5" />
                            {ctx.usageLoadingFor === o.id ? '…' : 'View'}
                          </button>
                          <button
                            type="button"
                            onClick={() => void ctx.onDeleteOrg(o)}
                            className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-sm font-bold text-rose-800 transition-colors hover:bg-rose-100"
                            title="Delete tenant from database"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
